import { useMemo } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { ISetCamera } from "../../../models/interactions/SetCamera";
import { updateSelectedInteraction } from "../../../app/slices/TrainingSlice";
import useSetCameraOptions, {
  findSetCameraOption,
  SetCameraOption,
} from "../../../hooks/SetCameraOptions.hook";
import PiChipSelect, { PiChipSelectOption } from "../../../components/PiChipSelect";
import { PiChipNeutralScope } from "../../../components/PiChip";
import CameraRenderPreview from "../../viewport3d/CameraRenderPreview";

// The same camera id can exist at several take locations, so options are keyed by both.
const optionKey = (option: SetCameraOption) =>
  `${option.context.takePositionId}/${option.id}`;

/** Camera dropdown for Set Camera: the take actor settings chip select, with a thumbnail per camera. */
export default function CameraPickerField(props: {
  setCamera: ISetCamera;
  /** Replaces the default (update the selected Set Camera interaction). */
  onPick?: (option: SetCameraOption) => void;
  /** Only list cameras of this take location (the take's starting camera). */
  takeLocationId?: string;
}) {
  const { setCamera, onPick, takeLocationId } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const allOptions = useSetCameraOptions(setCamera);
  const options = useMemo(
    () =>
      takeLocationId
        ? allOptions.filter((option) => option.context.takePositionId === takeLocationId)
        : allOptions,
    [allOptions, takeLocationId]
  );
  const selected = findSetCameraOption(options, setCamera);

  const chipOptions: PiChipSelectOption[] = useMemo(() => {
    const isGrouped =
      new Set(options.map((option) => option.context.takePositionId)).size > 1;
    return options.map((option) => ({
      id: optionKey(option),
      label: option.name,
      group: isGrouped
        ? option.context.takePositionName || option.context.takePositionId
        : undefined,
      icon: (
        <CameraRenderPreview
          cameraId={option.id}
          compact
          className="w-[32px] flex-none"
        />
      ),
    }));
  }, [options]);

  const onChange = (key: string) => {
    const option = options.find((o) => optionKey(o) === key);
    if (!option) return;
    if (onPick) {
      onPick(option);
      return;
    }
    dispatch(
      updateSelectedInteraction({
        ...setCamera,
        cameraId: option.id,
        takeLocationId: option.context.takeLocationId,
      } as ISetCamera)
    );
  };

  return (
    <PiChipNeutralScope>
      <PiChipSelect
        tone="setting"
        className="w-[280px]"
        aria-label={String(t("camera", "Camera"))}
        value={selected ? optionKey(selected) : ""}
        options={chipOptions}
        onChange={onChange}
        placeholder={
          setCamera.cameraId
            ? "Situational Camera"
            : String(t("set_camera_choose", "Choose camera"))
        }
      />
    </PiChipNeutralScope>
  );
}
